import React, { useEffect, useMemo, useState } from "react"
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonMenuButton,
  IonNote,
  IonPage,
  IonRange,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
  RefresherEventDetail,
  useIonViewWillEnter,
} from "@ionic/react"

import API, { RotationEvaluationAssignment, RotationEvaluationQuestionnaire } from "../helpers/API"
import {
  filterPendingRotationEvaluations,
  markRotationEvaluationSubmitted,
} from "../helpers/rotationEvaluationStorage"
import "./Evaluations.scss"

type AnswerMap = {
  [questionId: number]: {
    rating_value?: number | null
    text_value?: string
  }
}

const formatDate = (value?: string | null) => {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("it-IT")
}

const getDefaultRatingValue = (questionnaire: RotationEvaluationQuestionnaire) =>
  Math.round((questionnaire.scale_min + questionnaire.scale_max) / 2)

const getRatingLabel = (
  questionnaire: RotationEvaluationQuestionnaire,
  value?: number | null
) => {
  if (value == null) return ""
  const label = questionnaire.rating_labels?.[value - questionnaire.scale_min]
  return label ? `${label} (${value})` : `${value} / ${questionnaire.scale_max}`
}

const StudentEvaluations: React.FC = () => {
  const [assignments, setAssignments] = useState<RotationEvaluationAssignment[]>([])
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null)
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [isLoading, setIsLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

  const selectedAssignment = useMemo(
    () => assignments.find((assignment) => assignment.id === selectedAssignmentId),
    [assignments, selectedAssignmentId]
  )

  useIonViewWillEnter(() => {
    loadData()
  })

  useEffect(() => {
    if (!selectedAssignment) return
    const defaults: AnswerMap = {}
    selectedAssignment.questionnaire.questions.forEach((question) => {
      defaults[question.id] =
        question.question_type === "rating"
          ? { rating_value: getDefaultRatingValue(selectedAssignment.questionnaire) }
          : { text_value: "" }
    })
    setAnswers(defaults)
  }, [selectedAssignment])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const data = await API.getRotationEvaluationAssignments()
      setAssignments(filterPendingRotationEvaluations(data))
    } catch (error) {
      setToastMessage("Non riesco a caricare le valutazioni rotazioni.")
      setAssignments([])
    } finally {
      setIsLoading(false)
    }
  }

  const refreshData = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      await loadData()
    } finally {
      event.detail.complete()
    }
  }

  const selectAssignment = (assignment: RotationEvaluationAssignment) => {
    setSelectedAssignmentId(assignment.id)
  }

  const submitSelectedAssignment = async () => {
    if (!selectedAssignment) return
    const payload = selectedAssignment.questionnaire.questions.map((question) => {
      const answer = answers[question.id] || {}
      if (question.question_type === "rating") {
        return {
          question: question.id,
          rating_value: answer.rating_value ?? getDefaultRatingValue(selectedAssignment.questionnaire),
        }
      }
      return {
        question: question.id,
        text_value: answer.text_value || "",
      }
    })

    setIsLoading(true)
    try {
      await API.submitRotationEvaluation(selectedAssignment.id, payload)
      markRotationEvaluationSubmitted(selectedAssignment.id)
      setToastMessage("Risposta registrata in forma anonima.")
      setSelectedAssignmentId(null)
      await loadData()
    } catch (error) {
      setToastMessage("Non riesco a inviare il questionario.")
    } finally {
      setIsLoading(false)
    }
  }

  const renderAssignmentCard = (assignment: RotationEvaluationAssignment) => (
    <IonCard key={assignment.id}>
      <IonCardHeader>
        <IonCardSubtitle>
          {assignment.rotation_name || "Rotazione non impostata"} · {formatDate(assignment.period_start)} / {formatDate(assignment.period_end)}
        </IonCardSubtitle>
        <IonCardTitle>{assignment.questionnaire.name}</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {assignment.questionnaire.description ? (
          <p>{assignment.questionnaire.description}</p>
        ) : null}
        <IonButton expand="block" onClick={() => selectAssignment(assignment)}>
          Compila
        </IonButton>
      </IonCardContent>
    </IonCard>
  )

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text="" />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Valutazione Rotazioni</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={selectedAssignment ? "has-evaluation-footer" : ""}>
        <IonRefresher slot="fixed" onIonRefresh={refreshData}>
          <IonRefresherContent />
        </IonRefresher>
        <IonLoading isOpen={isLoading} message={"Carico..."} />

        {selectedAssignment ? (
          <IonCard className="evaluation-card">
            <IonCardHeader>
              <IonCardSubtitle>
                {selectedAssignment.rotation_name || "Rotazione non impostata"} · {formatDate(selectedAssignment.period_start)} / {formatDate(selectedAssignment.period_end)}
              </IonCardSubtitle>
              <IonCardTitle>{selectedAssignment.questionnaire.name}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText color="medium">
                Le risposte vengono salvate senza il tuo nome nell'export dei professori.
              </IonText>
              <IonList>
                {selectedAssignment.questionnaire.questions.map((question) => (
                  <IonItem
                    className="rotation-evaluation-question"
                    key={question.id}
                    lines="full"
                  >
                    <IonLabel className="ion-text-wrap rotation-evaluation-question-label">
                      <h2>{question.text}</h2>
                      {question.help_text ? <p>{question.help_text}</p> : null}
                      {question.question_type === "rating" ? (
                        <>
                          <IonNote className="rotation-evaluation-rating-note">
                            Voto: {getRatingLabel(
                              selectedAssignment.questionnaire,
                              answers[question.id]?.rating_value
                            )}
                          </IonNote>
                          <IonRange
                            min={selectedAssignment.questionnaire.scale_min}
                            max={selectedAssignment.questionnaire.scale_max}
                            snaps={true}
                            step={1}
                            value={answers[question.id]?.rating_value ?? undefined}
                            onIonChange={(event) =>
                              setAnswers({
                                ...answers,
                                [question.id]: {
                                  ...answers[question.id],
                                  rating_value: Number(event.detail.value),
                                },
                              })
                            }
                          />
                        </>
                      ) : (
                        <IonTextarea
                          autoGrow={true}
                          className="evaluation-answer-textarea"
                          value={answers[question.id]?.text_value || ""}
                          onIonChange={(event) =>
                            setAnswers({
                              ...answers,
                              [question.id]: {
                                ...answers[question.id],
                                text_value: event.detail.value || "",
                              },
                            })
                          }
                        />
                      )}
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            </IonCardContent>
            <IonFooter className="ion-padding evaluation-footer">
              <IonButton fill="outline" onClick={() => setSelectedAssignmentId(null)}>
                Annulla
              </IonButton>
              <IonButton className="ion-float-right" onClick={submitSelectedAssignment}>
                Invia anonimamente
              </IonButton>
            </IonFooter>
          </IonCard>
        ) : (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Questionari disponibili</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {assignments.length === 0 ? (
                <IonText color="medium">Nessun questionario attivo al momento.</IonText>
              ) : (
                assignments.map(renderAssignmentCard)
              )}
            </IonCardContent>
          </IonCard>
        )}

        <IonToast
          isOpen={!!toastMessage}
          message={toastMessage}
          duration={2500}
          onDidDismiss={() => setToastMessage("")}
        />
      </IonContent>
    </IonPage>
  )
}

export default StudentEvaluations
